import { mulberry32 } from './scorched-earth.rng';
import { SKY_HEIGHT, TANK_HEIGHT, TANK_WIDTH, WORLD_HEIGHT, WORLD_WIDTH } from './scorched-earth.constants';

export const TERRAIN_MIN_Y = SKY_HEIGHT * 0.2;
export const TERRAIN_MAX_Y = SKY_HEIGHT * 0.95;
export const MAX_TERRAIN_SLOPE = 3.5;
const MIN_SPAWN_SKY_CLEARANCE = TANK_HEIGHT + 48;

export interface PlayableSpawnRegion {
	startX: number;
	endX: number;
	centerX: number;
	avgSurfaceY: number;
	maxLocalSlope: number;
}

export interface TerrainStratum {
	depthStart: number;
	depthEnd: number;
	top: string;
	bottom: string;
}

export function buildStrataPalette(seed: number): TerrainStratum[] {
	const hueShift = (seed % 17) * 2;
	const warmBias = seed % 3 === 0 ? 6 : seed % 3 === 1 ? -4 : 0;
	const dryBias = seed % 5 === 0 ? -8 : seed % 5 === 2 ? 6 : 0;
	return [
		{ depthStart: 0, depthEnd: 0.08, top: '#7cb356', bottom: '#5a8a3c' },
		{ depthStart: 0.08, depthEnd: 0.22, top: '#6f5340', bottom: '#4a3528' },
		{ depthStart: 0.22, depthEnd: 0.48, top: '#9a7860', bottom: '#6b5040' },
		{
			depthStart: 0.48,
			depthEnd: 1,
			top: `rgb(${78 + hueShift + warmBias + dryBias}, ${72 + hueShift}, ${88 + hueShift - dryBias})`,
			bottom: `rgb(${46 + hueShift}, ${42 + hueShift}, ${56 + hueShift})`,
		},
	];
}

export function createScorchField(width = WORLD_WIDTH): Float32Array {
	return new Float32Array(width);
}

export function applyScorchMark(
	scorch: Float32Array,
	centerX: number,
	radius: number,
	intensity = 1,
): void {
	const r = Math.max(3, radius);
	const left = Math.max(0, Math.floor(centerX - r * 1.15));
	const right = Math.min(scorch.length - 1, Math.ceil(centerX + r * 1.15));
	for (let x = left; x <= right; x++) {
		const dx = x - centerX;
		const falloff = 1 - Math.abs(dx) / (r * 1.15);
		if (falloff > 0) {
			scorch[x] = Math.min(1, scorch[x] + intensity * falloff * falloff);
		}
	}
}

export function stratumAtDepth(strata: TerrainStratum[], depthNorm: number): TerrainStratum {
	const d = clamp(depthNorm, 0, 0.999);
	for (const layer of strata) {
		if (d >= layer.depthStart && d < layer.depthEnd) {
			return layer;
		}
	}
	return strata[strata.length - 1];
}

export function mixTerrainColors(base: string, blend: string, t: number): string {
	if (t <= 0) {
		return base;
	}
	if (t >= 1) {
		return blend;
	}
	const parse = (color: string): [number, number, number] => {
		if (color.startsWith('rgb')) {
			const m = color.match(/\d+/g);
			return m ? [+m[0], +m[1], +m[2]] : [0, 0, 0];
		}
		const h = color.replace('#', '');
		return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
	};
	const a = parse(base);
	const b = parse(blend);
	const mix = (i: number) => Math.round(a[i] * (1 - t) + b[i] * t);
	return `rgb(${mix(0)},${mix(1)},${mix(2)})`;
}

export function depthFromSurfaceAt(
	heights: Float32Array,
	x: number,
	y: number,
	worldHeight = WORLD_HEIGHT,
): number {
	const surface = sampleTerrainY(heights, x);
	if (y <= surface) {
		return 0;
	}
	const span = worldHeight - surface;
	if (span <= 0) {
		return 1;
	}
	return clamp((y - surface) / span, 0, 1);
}

export function terrainColorAt(
	heights: Float32Array,
	strata: TerrainStratum[],
	x: number,
	y: number,
	options: {
		scorch?: number;
		seed?: number;
		worldHeight?: number;
	} = {},
): string {
	const worldHeight = options.worldHeight ?? WORLD_HEIGHT;
	const scorch = options.scorch ?? 0;
	const seed = options.seed ?? 0;
	const surface = sampleTerrainY(heights, x);
	const slope = localSlopeAt(heights, x);
	const slopeRockBias = clamp(slope / MAX_TERRAIN_SLOPE, 0, 1) * 0.28;
	let depth = depthFromSurfaceAt(heights, x, y, worldHeight);
	depth = clamp(depth + slopeRockBias * (1 - depth * 0.65), 0, 1);
	const stratum = stratumAtDepth(strata, depth);
	const layerSpan = Math.max(stratum.depthEnd - stratum.depthStart, 0.001);
	const layerT = clamp((depth - stratum.depthStart) / layerSpan, 0, 1);
	let color = mixTerrainColors(stratum.top, stratum.bottom, layerT);
	const elevNorm = clamp(1 - surface / worldHeight, 0, 1);
	if (depth < 0.1) {
		color = mixTerrainColors(color, '#8fbc62', (elevNorm - 0.42) * 0.35);
		color = mixTerrainColors(color, '#4a3728', slopeRockBias * 0.55);
	}
	const strataGrain =
		(((seed * 131 + x * 17 + Math.floor(depth * 40)) % 100) / 100 - 0.5) * 0.035;
	color = mixTerrainColors(color, strataGrain > 0 ? '#ffffff' : '#000000', Math.abs(strataGrain));
	const scorchT = scorch * (1 - depth) * 0.72;
	return mixTerrainColors(color, '#1a1208', scorchT);
}

export function craterRimStrength(heights: Float32Array, x: number): number {
	if (x <= 0 || x >= heights.length - 1) {
		return 0;
	}
	const slope = Math.abs(heights[x + 1] - heights[x - 1]) * 0.5;
	return clamp(slope / 14, 0, 1);
}

export function maxColumnSlope(heights: Float32Array): number {
	let max = 0;
	for (let x = 1; x < heights.length; x++) {
		max = Math.max(max, Math.abs(heights[x] - heights[x - 1]));
	}
	return max;
}

export function localSlopeAt(heights: Float32Array, x: number): number {
	const ix = clamp(Math.floor(x), 0, heights.length - 1);
	if (ix <= 0) {
		return Math.abs(heights[1] - heights[0]);
	}
	if (ix >= heights.length - 1) {
		return Math.abs(heights[ix] - heights[ix - 1]);
	}
	return Math.abs(heights[ix + 1] - heights[ix - 1]) * 0.5;
}

export function terrainWithinSlopeLimit(heights: Float32Array, limit = MAX_TERRAIN_SLOPE): boolean {
	return maxColumnSlope(heights) <= limit + 1e-6;
}

export function findPlayableSpawnRegions(
	heights: Float32Array,
	options: {
		minWidth?: number;
		maxSlope?: number;
		minSkyClearance?: number;
	} = {},
): PlayableSpawnRegion[] {
	const minWidth = options.minWidth ?? TANK_WIDTH * 2.5;
	const maxSlope = options.maxSlope ?? MAX_TERRAIN_SLOPE;
	const minSkyClearance = options.minSkyClearance ?? MIN_SPAWN_SKY_CLEARANCE;
	const regions: PlayableSpawnRegion[] = [];
	let runStart = -1;
	let runMaxSlope = 0;
	let runSum = 0;
	let runCount = 0;

	const flushRun = (runEnd: number): void => {
		if (runStart < 0 || runCount < minWidth) {
			return;
		}
		const avgSurfaceY = runSum / runCount;
		if (avgSurfaceY > TERRAIN_MAX_Y - minSkyClearance) {
			return;
		}
		regions.push({
			startX: runStart,
			endX: runEnd,
			centerX: runStart + (runCount - 1) / 2,
			avgSurfaceY,
			maxLocalSlope: runMaxSlope,
		});
	};

	for (let x = 0; x < heights.length; x++) {
		const slope = localSlopeAt(heights, x);
		const hasSky = heights[x] <= TERRAIN_MAX_Y - minSkyClearance;
		if (slope <= maxSlope && hasSky && heights[x] >= TERRAIN_MIN_Y) {
			if (runStart < 0) {
				runStart = x;
				runMaxSlope = slope;
				runSum = heights[x];
				runCount = 1;
			} else {
				runMaxSlope = Math.max(runMaxSlope, slope);
				runSum += heights[x];
				runCount++;
			}
			continue;
		}
		if (runStart >= 0) {
			flushRun(x - 1);
			runStart = -1;
			runMaxSlope = 0;
			runSum = 0;
			runCount = 0;
		}
	}
	if (runStart >= 0) {
		flushRun(heights.length - 1);
	}
	return regions;
}

export function getCanonicalSpawnXs(count = 4, width = WORLD_WIDTH): number[] {
	const margin = 48;
	const spacing = (width - margin * 2) / (count + 1);
	return Array.from({ length: count }, (_, i) => margin + spacing * (i + 1));
}

export function isPlayableSpawnAt(
	heights: Float32Array,
	x: number,
	halfWidth = TANK_WIDTH * 1.25,
): boolean {
	const left = Math.max(0, Math.floor(x - halfWidth));
	const right = Math.min(heights.length - 1, Math.ceil(x + halfWidth));
	let maxSlope = 0;
	let sum = 0;
	for (let i = left; i <= right; i++) {
		maxSlope = Math.max(maxSlope, localSlopeAt(heights, i));
		sum += heights[i];
	}
	const avg = sum / (right - left + 1);
	return (
		maxSlope <= MAX_TERRAIN_SLOPE &&
		avg >= TERRAIN_MIN_Y &&
		avg <= TERRAIN_MAX_Y - MIN_SPAWN_SKY_CLEARANCE
	);
}

export function enforceMaxSlope(heights: Float32Array, maxSlope: number): void {
	for (let x = 1; x < heights.length; x++) {
		const delta = heights[x] - heights[x - 1];
		if (delta > maxSlope) {
			heights[x] = heights[x - 1] + maxSlope;
		} else if (delta < -maxSlope) {
			heights[x] = heights[x - 1] - maxSlope;
		}
	}
	for (let x = heights.length - 2; x >= 0; x--) {
		const delta = heights[x] - heights[x + 1];
		if (delta > maxSlope) {
			heights[x] = heights[x + 1] + maxSlope;
		} else if (delta < -maxSlope) {
			heights[x] = heights[x + 1] - maxSlope;
		}
	}
}

export function smoothNeedlePeaks(heights: Float32Array, maxSlope = MAX_TERRAIN_SLOPE): void {
	const copy = heights.slice();
	for (let x = 1; x < heights.length - 1; x++) {
		const leftDelta = copy[x] - copy[x - 1];
		const rightDelta = copy[x + 1] - copy[x];
		const spike =
			(leftDelta > maxSlope && rightDelta < -maxSlope) ||
			(leftDelta < -maxSlope && rightDelta > maxSlope);
		if (!spike) {
			continue;
		}
		heights[x] = (copy[x - 1] + copy[x] + copy[x + 1]) / 3;
	}
}

export function generateTerrain(seed: number, width = WORLD_WIDTH): Float32Array {
	const heights = new Float32Array(width);
	const rand = mulberry32(seed >>> 0);
	const base = SKY_HEIGHT * (0.52 + (rand() - 0.5) * 0.1);
	const macroAmp = SKY_HEIGHT * (0.14 + rand() * 0.1);
	const midAmp = SKY_HEIGHT * (0.05 + rand() * 0.035);
	const detailAmp = 2.5 + rand() * 3.5;
	const landform = seed % 5;

	const anchorCount = 8 + Math.floor(rand() * 5);
	const anchors: number[] = [];
	for (let i = 0; i < anchorCount; i++) {
		anchors.push(rand() * 2 - 1);
	}

	const ridgeWavelength = 0.011 + rand() * 0.014;
	const ridgePhase = rand() * Math.PI * 2;
	const ridgeStrength = macroAmp * (0.35 + rand() * 0.35);
	const midPhase1 = rand() * Math.PI * 2;
	const midPhase2 = rand() * Math.PI * 2;
	const hasPlateau = rand() > 0.3;
	const plateauStart = width * (0.28 + rand() * 0.12);
	const plateauEnd = width * (0.52 + rand() * 0.18);
	const plateauDrop = 10 + rand() * 16;
	const mesaCenter = width * (0.18 + rand() * 0.22);
	const mesaHalf = width * (0.06 + rand() * 0.05);
	const mesaLift = 8 + rand() * 12;
	const canyonCenter = width * (0.45 + rand() * 0.18);
	const canyonHalf = width * (0.04 + rand() * 0.03);
	const canyonDepth = 14 + rand() * 18;
	const detailPhase1 = seed * 0.017;
	const detailPhase2 = seed * 0.043;

	for (let x = 0; x < width; x++) {
		const t = x / Math.max(1, width - 1);
		const anchorT = t * (anchorCount - 1);
		const i0 = Math.floor(anchorT);
		const i1 = Math.min(i0 + 1, anchorCount - 1);
		const frac = anchorT - i0;
		const smoothFrac = frac * frac * (3 - 2 * frac);
		const macro = anchors[i0] * (1 - smoothFrac) + anchors[i1] * smoothFrac;

		const ridge = Math.sin(x * ridgeWavelength + ridgePhase) * ridgeStrength;
		const valley = Math.sin(x * (ridgeWavelength * 0.55) + ridgePhase * 1.7) * (ridgeStrength * 0.45);
		const mid =
			Math.sin(x * 0.036 + midPhase1) * midAmp +
			Math.sin(x * 0.068 + midPhase2) * (midAmp * 0.42);
		const detail =
			Math.sin(x * 0.41 + detailPhase1) * detailAmp * 0.45 +
			Math.sin(x * 0.93 + detailPhase2) * detailAmp * 0.32;

		let y = base + macro * macroAmp + ridge + valley + mid + detail;
		if (hasPlateau && x >= plateauStart && x <= plateauEnd) {
			const plateauT = clamp((x - plateauStart) / Math.max(1, plateauEnd - plateauStart), 0, 1);
			const edgeBlend = Math.sin(plateauT * Math.PI);
			y -= plateauDrop * edgeBlend;
		}
		if (landform === 1) {
			const mesaT = clamp(1 - Math.abs(x - mesaCenter) / mesaHalf, 0, 1);
			y -= mesaLift * mesaT * mesaT;
		} else if (landform === 2) {
			const canyonT = clamp(1 - Math.abs(x - canyonCenter) / canyonHalf, 0, 1);
			y += canyonDepth * canyonT * canyonT;
		} else if (landform === 3) {
			y += Math.sin(x * 0.024 + ridgePhase) * macroAmp * 0.22;
		} else if (landform === 4) {
			y += Math.sin(x * 0.015 + midPhase1) * midAmp * 0.85;
		}
		heights[x] = clamp(y, TERRAIN_MIN_Y, TERRAIN_MAX_Y);
	}

	smoothTerrain(heights, 1);
	enforceMaxSlope(heights, MAX_TERRAIN_SLOPE);
	smoothNeedlePeaks(heights, MAX_TERRAIN_SLOPE);
	enforceMaxSlope(heights, MAX_TERRAIN_SLOPE);
	ensurePlayableSpawnZones(heights, width);
	clampTerrainBounds(heights);
	return heights;
}

function ensurePlayableSpawnZones(heights: Float32Array, width: number): void {
	const margin = 48;
	const spawnCount = 4;
	const spacing = (width - margin * 2) / (spawnCount + 1);
	const targetY = SKY_HEIGHT * (0.58 + 0.08);
	for (let i = 0; i < spawnCount; i++) {
		const centerX = margin + spacing * (i + 1);
		gentleFlattenRegion(heights, centerX, TANK_WIDTH * 2.75, targetY);
	}
	enforceMaxSlope(heights, MAX_TERRAIN_SLOPE);
}

function gentleFlattenRegion(
	heights: Float32Array,
	centerX: number,
	halfWidth: number,
	targetY: number,
): void {
	const left = Math.max(0, Math.floor(centerX - halfWidth));
	const right = Math.min(heights.length - 1, Math.ceil(centerX + halfWidth));
	for (let x = left; x <= right; x++) {
		const dx = (x - centerX) / halfWidth;
		const falloff = Math.max(0, 1 - dx * dx);
		const blend = falloff * 0.82;
		heights[x] = heights[x] * (1 - blend) + targetY * blend;
	}
}

function clampTerrainBounds(heights: Float32Array): void {
	for (let x = 0; x < heights.length; x++) {
		heights[x] = clamp(heights[x], TERRAIN_MIN_Y, TERRAIN_MAX_Y);
	}
}

export function smoothTerrain(heights: Float32Array, passes: number): void {
	for (let p = 0; p < passes; p++) {
		const copy = heights.slice();
		for (let x = 1; x < heights.length - 1; x++) {
			heights[x] = (copy[x - 1] + copy[x] * 2 + copy[x + 1]) / 4;
		}
	}
}

export function getSurfaceY(heights: Float32Array, x: number): number {
	const ix = clamp(Math.floor(x), 0, heights.length - 1);
	return heights[ix];
}

export function sampleTerrainY(heights: Float32Array, x: number): number {
	const clamped = clamp(x, 0, heights.length - 1);
	const x0 = Math.floor(clamped);
	const x1 = Math.min(x0 + 1, heights.length - 1);
	const t = clamped - x0;
	return heights[x0] * (1 - t) + heights[x1] * t;
}

export function carveCrater(
	heights: Float32Array,
	centerX: number,
	centerY: number,
	radius: number,
): void {
	const r = Math.max(4, radius);
	const left = Math.max(0, Math.floor(centerX - r));
	const right = Math.min(heights.length - 1, Math.ceil(centerX + r));
	for (let x = left; x <= right; x++) {
		const dx = x - centerX;
		const edge = Math.sqrt(Math.max(0, r * r - dx * dx));
		const craterFloor = centerY + edge * 0.35;
		if (craterFloor > heights[x]) {
			heights[x] = craterFloor;
		}
	}
	smoothTerrain(heights, 1);
}

export function isTerrainAt(heights: Float32Array, x: number, y: number): boolean {
	if (x < 0 || x >= heights.length || y < 0) {
		return false;
	}
	return y >= sampleTerrainY(heights, x);
}

export function placeTankOnTerrain(heights: Float32Array, x: number): number {
	return sampleTerrainY(heights, x);
}

export function terrainColumnDepth(heights: Float32Array, x: number, worldHeight = WORLD_HEIGHT): number {
	const surface = sampleTerrainY(heights, x);
	return clamp((worldHeight - surface) / worldHeight, 0, 1);
}

function clamp(v: number, min: number, max: number): number {
	return Math.max(min, Math.min(max, v));
}
